'use client';

import { trackEvent, TRACKING_CATEGORIES } from 'utils';
import Text, { TEXT_TAG, TEXT_VARIANT } from '@pixum/text';
import { Image } from '../../image';
import { LinkWithMasking } from '../../link/Link';
import type { AnchorLinkProps } from './../types';
import './../styles.css';
import { getCurrentUrl } from '@pixum/auth-core-ui';


/**
 * AnchorLink Component
 *
 * Renders an individual anchor link item with an image and optional title.
 * Uses LinkWithMasking to handle both internal and external links with proper masking behavior.
 *
 * @param props - AnchorLink properties including image, link, title, subtitle, and device
 * @param props.image - The image node to display
 * @param props.link - The link configuration with URL and masking settings
 * @param props.title - Optional title text displayed below the image
 * @param props.subtitle - Optional subtitle text displayed below the title
 * @param props.device - Device type (mobile or desktop) for responsive rendering
 * @returns A JSX element rendering an anchor link with image and title
 */
export function AnchorLink(props: AnchorLinkProps) {
  const { image, link, title, subtitle, device } = props;
  const isMobile = device === 'mobile';

  const handleClick = () => {
    const currentUrl = getCurrentUrl();
    trackEvent(TRACKING_CATEGORIES.JUMP_LINK, currentUrl, title ?? '');
  };

  return (
    <li className="anchor-link">
      <LinkWithMasking href={link?.url} className="anchor-link__content" masked={link?.masked} onClick={handleClick}>
        <Image image={image} device={device} className="anchor-link__image" />
        {title && (
          <Text tag={TEXT_TAG.SPAN} variant={TEXT_VARIANT.HEADLINE}>
            {title}
          </Text>
        )}
        {subtitle && !isMobile && <p>{subtitle}</p>}
      </LinkWithMasking>
    </li>
  );
}
